import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { Controller, useForm } from 'react-hook-form';
import {
  FileCategoryEnum,
  FileFormatEnum,
  UserContentFilesSkillsInsertInput,
  useInsertCoachPremiumContentMutation,
  useUpdateCoachPremiumContentMutation,
} from 'types/generated/client';
import { useUploadVideoFile } from 'hooks/useUploadVideoFile';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import FileUploader from 'components/VideoAnalysis/FileUploader';
import Skills from 'components/VideoAnalysis/Skills';
import Modal from 'components/modals/Modal';
import FilterBox from './FilterBox';
import TextInputField from './TextInputField';
import { UploadContentFormValues, UploadContentModalProps } from './props';
import {
  contentAccessOptions,
  defaultUploadContentValues,
  separatePremiumSkillsByType,
  uploadContentSchema,
} from './utils';

const UploadContentModal: React.FC<UploadContentModalProps> = ({
  open,
  handleClose,
  initialValues,
  onSuccessUpload,
}) => {
  const { uploadVideoFile, uploadProgress } = useUploadVideoFile();
  const [insertCoachPremiumContent, { loading: insertCoachPremiumContentLoading }] =
    useInsertCoachPremiumContentMutation();

  const [updateCoachPremiumContent, { loading: updateCoachPremiumContentLoading }] =
    useUpdateCoachPremiumContentMutation();
  const viewer = useViewer();

  const editMode = !!initialValues;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    getValues,
    setValue,
  } = useForm<UploadContentFormValues>({
    resolver: zodResolver(uploadContentSchema),
    defaultValues: defaultUploadContentValues,
  });

  const handleMediaUpload = async (files: File[], onChange: any) => {
    try {
      const file = files[0];

      const fileUploadRes = await uploadVideoFile({ file });

      if (!fileUploadRes) {
        throw new Error('Video upload failed.');
      }

      const formattedFile = {
        fileCategory: FileCategoryEnum.Coach,
        fileName: fileUploadRes.fileName,
        fileType: fileUploadRes.fileType,
        host: fileUploadRes.host,
        originalFileName: fileUploadRes.originalFileName,
        provider: fileUploadRes.provider,
        providerUrl: fileUploadRes.providerUrl,
        fileFormat: FileFormatEnum.Video,
        url: fileUploadRes.url,
        providerId: '',
        userId: viewer.userId,
      };

      onChange(formattedFile);
    } catch (error) {
      console.error('Media upload failed:', error);
      setError('videoFile', {
        type: 'manual',
        message: 'Error uploading media. Please try again.',
      });
    }
  };

  const handleSubmitContent = async (values: UploadContentFormValues) => {
    separatePremiumSkillsByType;
    try {
      const payload = {
        title: values.title,
        description: values.description,
        skills: separatePremiumSkillsByType(
          values.skills,
          viewer.userId!,
          values?.videoFile?.id,
        ) as UserContentFilesSkillsInsertInput[],
        tier: values.contentAccess,
        ...values.videoFile,
      };

      if (editMode) {
        payload.id = initialValues?.id;
        payload.userId = viewer.userId;
      }

      const res = editMode
        ? await updateCoachPremiumContent({
            variables: payload,
          })
        : await insertCoachPremiumContent({
            variables: payload,
          });
      if (!res.errors?.length) {
        reset(defaultUploadContentValues);
        onSuccessUpload();
        handleClose();
      }
    } catch (err) {
      captureException(err);
    }
  };

  useEffect(() => {
    if (initialValues) {
      const { title, description, userContentFilesSkills, tier, user, ...videoFile } =
        initialValues;
      reset({
        title: title,
        description: description,
        skills: userContentFilesSkills.map(
          (skill) => skill.userContentSkills.skill || skill.userContentSkills.customSkill || '',
        ),
        contentAccess: tier,
        videoFile,
      });
    } else {
      reset(defaultUploadContentValues);
    }
  }, [initialValues, reset]);

  return (
    <Modal isOpen={open} handleClose={handleClose} classNameMaxWidth="max-w-[40rem]">
      <div className="flex items-center justify-between border-b px-ds-xl py-ds-lg">
        <h2 className="typography-product-heading-desktop">Upload Video</h2>
        <ButtonText onClick={handleClose}>
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
      </div>
      <form onSubmit={handleSubmit(handleSubmitContent)}>
        <div className="flex flex-col gap-6 p-ds-xl">
          <Controller
            control={control}
            name="videoFile"
            render={({ field, fieldState: { error } }) => (
              <div>
                <span className="typography-product-heading-compact-desktop mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Add video file
                </span>
                <FileUploader
                  multiple={false}
                  uploadProgress={uploadProgress}
                  selectedFiles={field.value ? [field.value] : []}
                  onRemoveFile={() => field.onChange(null)}
                  onSelectFiles={(files) => handleMediaUpload(files, field.onChange)}
                  helperText="MP4 or MOV (max. 60 minute video)"
                  accept={{
                    'video/*': ['.mp4', '.mov'],
                  }}
                />
                {error && <p className="col-span-2 text-sm text-red-600 ">{error.message}</p>}
              </div>
            )}
          />
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TextInputField
                id="title"
                label="Title"
                placeholder="Title"
                type="text"
                error={errors.title?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <TextInputField
                id="description"
                label="Description"
                placeholder="Description"
                type="textarea"
                error={errors.description?.message}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="contentAccess"
            render={({ field: { value, onChange } }) => (
              <div>
                <span className="typography-product-heading-compact-desktop mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Content Access
                </span>
                <div className="flex gap-2">
                  {contentAccessOptions.map((option) => (
                    <FilterBox
                      key={option.value}
                      label={option.label}
                      value={option.value}
                      selected={value === option.value}
                      onClick={() => onChange(option.value)}
                    />
                  ))}
                </div>
              </div>
            )}
          />

          <div>
            <h3 className="typography-product-heading-compact-desktop mb-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Add Tags
            </h3>
            <div className="flex flex-col gap-4">
              {/* will be used later */}
              {/* <SportsSelect
                fieldName="sports"
                control={control}
                labelText="Sports"
                allowCustom={true}
              /> */}
              <Skills
                fieldName="skills"
                control={control}
                labelText="Skills"
                allowCustom={true}
                getValues={getValues}
                setValue={setValue}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t px-ds-xl py-ds-lg">
          <Button
            type="submit"
            variant="primary"
            className="w-fit"
            size="lg"
            disabled={insertCoachPremiumContentLoading || updateCoachPremiumContentLoading}
          >
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UploadContentModal;
